<script setup lang="ts">
import { computed, watch } from 'vue';
import Chip from 'primevue/chip';
import MultiSelect from 'primevue/multiselect';
import LeadFilterButton from '@apparatix/leads/LeadFilterButton.vue';
import FilterSelector from '@apparatix/leads/FilterSelector.vue';
import ToggleSwitch from 'primevue/toggleswitch';
import type { OpportunityType, Swimlane, ViewFormat } from '@apparatix/types/Leads';
import { useOpportunities } from '@apparatix/composables';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import Button from 'primevue/button';
import Select from 'primevue/select';
import ColumnDisplaySelector from '@apparatix/library/ColumnDisplaySelector.vue';

const props = defineProps<{ type: OpportunityType, currentView: ViewFormat }>();
const {
  updateSelectedView, addOrRemoveOwnerFromFilterGroup, saveLanesVisible, loadOpportunities,
} = useOpportunities();
const store = useOpportunityStore();

const onVisibleLanesChange = (lanes: Swimlane[]) => saveLanesVisible(props.type, lanes);

const laneFilterLabel = computed(() => {
  const hiddenCount = store.swimlanes[props.type].length - store.visibleSwimlanes[props.type].length;
  if (hiddenCount === 0) {
    return 'Configure visible lanes';
  }
  if (hiddenCount === 1) {
    return '1 lane hidden';
  }

  return `${hiddenCount} lanes hidden`;
});

// @TODO: Refactor to avoid side effects - reference MR !6887 if questions
const showFilters = computed({
  get: () => store.filters[props.type].isVisible,
  set: (value: boolean) => {
    if (value) {
      store.showTableFilters(props.type);
    } else {
      store.hideTableFilters(props.type);
    }
  },
});

const handleStateChange = async () => {
  store.protectMasterList = false;
  store.stateReady[props.type] = false;
  await loadOpportunities(props.type, 'high', store.currentState[props.type]);
  store.protectMasterList = true;
};

// Start Column Configuration Leads custom Logic
watch(
  () => store.tableRefs[props.type]?.d_columnOrder,
  () => {
    if (store.tableRefs[props.type] && !store.tableRefs[props.type].d_columnOrder) {
      // eslint-disable-next-line camelcase
      store.tableRefs[props.type].d_columnOrder = store.tableColumns[props.type].map(column => column.field);
    }
  },
);

const handleVisibleColumnChange = (updatedColumns) => {
  store.visibleColumns[props.type] = updatedColumns;
};

const handleTableColumnChange = (updatedColumns) => {
  store.tableColumns[props.type] = updatedColumns;
  // eslint-disable-next-line camelcase
  store.tableRefs[props.type].d_columnOrder = store.tableColumns[props.type].map(column => column.field);
};
// End Column Configuration Leads custom Logic

</script>
<template>
  <div class="flex">
    <div class="flex gap-4 grow">
      <div v-if="currentView === 'table'" class="flex items-center justify-center gap-2">
        <span>Filters</span>
        <ToggleSwitch v-model="showFilters" />
        <Button
          v-tooltip.left="'Clear Filters'"
          size="small"
          outlined
          severity="secondary"
          :disabled="!(showFilters && store.isFilterChanged(props.type))"
          @click="store.resetFilters(props.type)"
        >
          <i class="pi pi-filter-slash" />
          <span class="font-bold">Clear Filters</span>
        </Button>
      </div>
      <FilterSelector v-if="currentView === 'swimlanes'" :type="props.type" />
      <div v-if="store.currentlySelectedOwnersForFilter[props.type].length > 0 && currentView === 'swimlanes'" class="flex gap-2">
        <Chip
          v-for="owner in store.currentlySelectedOwnersForFilter[props.type]"
          :key="owner.id"
          style="color: var(--primary-text-blue); background: var(--swimlane-filter-button-bg)"
          :label="owner.name"
          removable
          @remove="addOrRemoveOwnerFromFilterGroup(props.type, owner)"
        />
      </div>
    </div>
    <div class="flex flex-none gap-1">
      <MultiSelect
        v-if="currentView === 'swimlanes' && store.swimlanes[props.type].length > 0"
        v-model="store.selectedVisibleLanes[props.type]"
        class="hidden lg:flex"
        :options="store.swimlanes[props.type]"
        :max-selected-labels="0"
        option-label="status"
        :placeholder="laneFilterLabel"
        :selected-items-label="laneFilterLabel"
        @update:model-value="onVisibleLanesChange"
      />
      <ColumnDisplaySelector
        v-if="currentView === 'table'"
        :default-table-columns="store.defaultTableColumns[props.type]"
        :default-visible-columns="store.defaultVisibleColumns[props.type]"
        :table-columns="store.tableColumns[props.type]"
        :visible-columns="store.visibleColumns[props.type]"
        :storage-prefix="store.storagePrefix[props.type]"
        @update:visible-columns="handleVisibleColumnChange"
        @update:table-columns="handleTableColumnChange"
      />
      <LeadFilterButton name="grid" :selected="currentView === 'table'" @click="updateSelectedView(props.type, 'table')" />
      <LeadFilterButton name="swimlane" :selected="currentView === 'swimlanes'" @click="updateSelectedView(props.type, 'swimlanes')" />
      <Select
        v-model="store.currentState[props.type]"
        fluid
        :options="store.options[props.type].availableStates"
        class="md:w-14rem"
        @change="handleStateChange"
      >
        <template #value="slotProps">
          <div v-if="slotProps.value" class="flex items-center font-bold">
            <i v-if="slotProps.value === 'Archived'" class="mr-2 pi pi-circle-fill text-red-500 bg-red-200 rounded-full" />
            <i v-else-if="slotProps.value === 'Completed'" class="mr-2 pi pi-circle-fill text-gray-500 bg-gray-200 rounded-full" />
            <i v-else-if="slotProps.value === 'Inactive'" class="mr-2 pi pi-circle-fill text-yellow-500 bg-yellow-200 rounded-full" />
            <i v-else class="mr-2 pi pi-circle-fill text-green-500 bg-green-200 rounded-full" />
            <div>{{ slotProps.value }}</div>
          </div>
        </template>
        <template #option="slotProps">
          <div class="flex items-center">
            <i v-if="slotProps.option == 'Archived'" class="mr-2 pi pi-circle-fill text-red-500 bg-red-200 rounded-full" />
            <i v-else-if="slotProps.option === 'Completed'" class="mr-2 pi pi-circle-fill text-gray-500 bg-gray-200 rounded-full" />
            <i v-else-if="slotProps.option === 'Inactive'" class="mr-2 pi pi-circle-fill text-yellow-500 bg-yellow-200 rounded-full" />
            <i v-else class="mr-2 pi pi-circle-fill text-green-500 bg-green-200 rounded-full" />
            <div>{{ slotProps.option }}</div>
          </div>
        </template>
      </Select>
    </div>
  </div>
</template>

<style scoped>
.p-button-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}
</style>
